import { CSVFileEntry } from '../../../types/common';

/**
 * CSV Metadata extracted from file content
 */
export interface CSVMetadata {
  utcMin: string;
  utcMax: string;
  zeitschrittweite: string;
  min: string;
  max: string;
  offset: string;
  datenpunkte: string;
  numerischeDatenpunkte: string;
  numerischerAnteil: string;
}

/**
 * Creates an empty CSVFileEntry with default values
 * @param fileType - 'input' for CSV input files, 'output' for output files
 */
export const createEmptyEntry = (fileType: 'input' | 'output' = 'input'): CSVFileEntry => ({
  id: Date.now().toString(),
  file: null,
  fileName: '',
  bezeichnung: '',
  utcMin: '',
  utcMax: '',
  zeitschrittweite: '',
  min: '',
  max: '',
  offset: '',
  datenpunkte: '',
  numerischeDatenpunkte: '',
  numerischerAnteil: '',
  datenform: fileType === 'output' ? 'Historische Daten' : '',
  zeithorizontStart: '',
  zeithorizontEnd: '',
  zeitschrittweiteTransferiertenDaten: '',
  offsetTransferiertenDaten: '',
  mittelwertbildungÜberDenZeithorizont: 'nein',
  datenanpassung: 'Lineare Interpolation',
  zeitschrittweiteMinValue: '',
  zeitschrittweiteAvgValue: '',
  skalierung: 'ja',
  skalierungMin: '0',
  skalierungMax: '1',
  storagePath: '',
  type: fileType,
  createdAt: '',
  updatedAt: ''
});

/**
 * Sanitize values - convert "None" strings and null/undefined to empty strings
 */
export const sanitizeValue = (value: unknown): string => {
  if (value === null || value === undefined || value === 'None') {
    return '';
  }
  return String(value);
};

/**
 * Format date in "YYYY-MM-DD HH:mm:ss" format
 */
export const formatDate = (date: Date): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/**
 * Process CSV content to extract metadata using Python backend logic
 * This is a pure function that can be tested independently
 */
export const processCSVContent = (content: string): CSVMetadata => {
  const lines = content.split('\n').filter(line => line.trim() !== '');
  
  // Default values
  const metadata: CSVMetadata = {
    utcMin: '',
    utcMax: '',
    zeitschrittweite: '',
    min: '',
    max: '',
    offset: '',
    datenpunkte: '',
    numerischeDatenpunkte: '',
    numerischerAnteil: ''
  };
  
  if (lines.length < 2) return metadata; // Need at least header and one data row
  
  try {
    // Parse CSV data
    const data = lines.slice(1).map(line => line.split(';').map(cell => cell.trim()));
    
    if (data.length === 0) return metadata;
    
    // n_all = total number of data points
    const n_all = data.length;
    metadata.datenpunkte = n_all.toString();
    
    // Extract UTC timestamps from first column
    const timestamps = data.map(row => row[0]).filter(ts => ts);
    
    if (timestamps.length > 0) {
      // utc_min = df["UTC"].iloc[0] - FIRST row timestamp (not minimum)
      const firstTimestamp = new Date(timestamps[0]);
      if (!isNaN(firstTimestamp.getTime())) {
        metadata.utcMin = formatDate(firstTimestamp);
      }
      
      // utc_max = df["UTC"].iloc[-1] - LAST row timestamp (not maximum)
      const lastTimestamp = new Date(timestamps[timestamps.length - 1]);
      if (!isNaN(lastTimestamp.getTime())) {
        metadata.utcMax = formatDate(lastTimestamp);
        
        // delt = time step width in minutes using Python formula:
        // delt = (df["UTC"].iloc[-1] - df["UTC"].iloc[0]).total_seconds() / (60 * (n_all - 1))
        if (n_all > 1 && !isNaN(firstTimestamp.getTime())) {
          const timeDiffMs = lastTimestamp.getTime() - firstTimestamp.getTime();
          const timeDiffMinutes = timeDiffMs / (1000 * 60); // Convert to minutes
          const delt = timeDiffMinutes / (n_all - 1);
          metadata.zeitschrittweite = delt.toString();
          
          // Calculate offset using Python logic
          // Check if delt allows constant offset (round(60/delt) == 60/delt)
          if (delt > 0) {
            const ratio = 60 / delt;
            const roundedRatio = Math.round(ratio);
            
            // Konstanter Offset
            if (Math.abs(ratio - roundedRatio) < 0.000001) { // Floating point comparison
              // Create hour start time (same hour, but 0 minutes, 0 seconds, 0 ms)
              const hourStart = new Date(firstTimestamp);
              hourStart.setMinutes(0, 0, 0);
              
              // ofst = (first_timestamp - hour_start).total_seconds() / 60
              let ofst = (firstTimestamp.getTime() - hourStart.getTime()) / (1000 * 60);
              
              // while ofst - delt >= 0: ofst -= delt (with safety limit)
              let iterations = 0;
              while (ofst - delt >= 0 && iterations < 10000) {
                ofst -= delt;
                iterations++;
              }
              
              metadata.offset = ofst.toString();
            } else {
              // Variabler Offset
              metadata.offset = 'var';
            }
          } else {
            metadata.offset = '0';
          }
        } else {
          metadata.zeitschrittweite = '0';
          metadata.offset = '0';
        }
      }
    }
    
    // Count numeric data points using exact Python logic
    let n_num = n_all; // Start with n_all
    
    // Python logic: n_num = n_all; for i in range(n_all): try: float(df.iloc[i, 1]); if math.isnan(float(df.iloc[i, 1])): n_num -= 1; except: n_num -= 1
    for (let i = 0; i < data.length; i++) {
      if (data[i].length > 1) {
        try {
          // First float() conversion attempt - like Python
          const val = parseFloat(data[i][1]);
          // Check if the conversion result is NaN - like Python math.isnan()
          if (isNaN(val)) {
            n_num -= 1;
          }
        } catch {
          n_num -= 1;
        }
      } else {
        n_num -= 1;
      }
    }
    
    metadata.numerischeDatenpunkte = n_num.toString();
    
    // rate_num = round(n_num/n_all*100, 2)
    if (n_all > 0) {
      const rate_num = Math.round((n_num / n_all * 100) * 100) / 100; // Round to 2 decimals
      metadata.numerischerAnteil = rate_num.toFixed(2);
    } else {
      metadata.numerischerAnteil = '0.00';
    }
    
    // Extract min and max values from second column - val_min and val_max
    // Use pandas-like logic: df.iloc[:, 1].min() and df.iloc[:, 1].max()
    if (data.length > 0) {
      let val_min = Infinity;
      let val_max = -Infinity;
      let hasValidNumbers = false;
      
      // Process all rows in second column like pandas does
      for (let i = 0; i < data.length; i++) {
        if (data[i].length > 1) {
          try {
            const val = parseFloat(data[i][1]);
            if (!isNaN(val)) {
              hasValidNumbers = true;
              if (val < val_min) val_min = val;
              if (val > val_max) val_max = val;
            }
          } catch {
            // Skip invalid values like pandas does
          }
        }
      }
      
      if (hasValidNumbers && val_min !== Infinity && val_max !== -Infinity) {
        metadata.min = val_min.toString();
        metadata.max = val_max.toString();
      }
    }
    
  } catch (error) {
    // Log error but don't call setState during render
    console.error('Error processing CSV content:', error);
  }
  
  return metadata;
};

/**
 * Calculate transferred data parameters (delt_transf and ofst_transf)
 * Based on Python backend formula from:
 * /Backend_RabensteinerEng/my_backend/OriginalTraining/utils/time_utils.py
 *
 * @param th_strt - Zeithorizont Start [h] (user input)
 * @param th_end - Zeithorizont Ende [h] (user input)
 * @param csvOffset - Offset from CSV metadata (entry.offset)
 * @param N - Anzahl der Zeitschritte (MTS.I_N from AllgemeineDaten)
 * @returns { delt_transf, ofst_transf } - calculated values
 */
export const calculateTransferredData = (
  th_strt: number,
  th_end: number,
  csvOffset: number,
  N: number = 13 // MTS.I_N / MTS.O_N - Anzahl der Zeitschritte (from AllgemeineDaten.eingabe)
): { delt_transf: number; ofst_transf: number | string } => {
  const OFST = csvOffset; // Offset from CSV metadata

  // delt_transf = (th_end - th_strt) * 60 / (N - 1)
  const delt_transf = (th_end - th_strt) * 60 / (N - 1);

  // ofst_transf calculation
  let ofst_transf: number | string;

  // Check if offset can be calculated: round(60/delt_transf) == 60/delt_transf
  const ratio = 60 / delt_transf;
  const roundedRatio = Math.round(ratio);

  if (Math.abs(ratio - roundedRatio) < 0.000001) {
    // Offset can be calculated
    // ofst_transf = OFST - (th_strt - floor(th_strt)) * 60 + 60
    ofst_transf = OFST - (th_strt - Math.floor(th_strt)) * 60 + 60;

    // while ofst_transf - delt_transf >= 0: ofst_transf -= delt_transf
    let iterations = 0;
    while (ofst_transf - delt_transf >= 0 && iterations < 10000) {
      ofst_transf -= delt_transf;
      iterations++;
    }
  } else {
    // Offset cannot be calculated
    ofst_transf = 'var';
  }

  return { delt_transf, ofst_transf };
};
